import { mapOptionSyncErrors } from '../../patterns';
import {
  AppointmentProposalOption,
  AppointmentProposalOptionCosts,
} from './appointmentProposalOptions.models';

export interface AppointmentProposalOptionsState {
  ids: string[];
  entities: Record<string, AppointmentProposalOption>;
  costBreakdown: Record<string, AppointmentProposalOptionCosts>;
  selectedId: string | null;
  selectedProposalId: string | null;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  loadedProposalIds: string[];
}

export const initState: AppointmentProposalOptionsState = {
  ids: [],
  entities: {},
  costBreakdown: {},
  selectedId: null,
  selectedProposalId: null,
  error: null,
  isLoaded: false,
  isLoading: true,
  loadedProposalIds: [],
};

type Action =
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS';
      payload: {
        appointmentProposalOptions: AppointmentProposalOption[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE';
      payload: { error: string };
    }
  | { type: 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_CANCELLED' }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION';
      payload: {
        appointmentProposalId: string;
        appointmentProposalOptionId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_SUCCESS';
      payload: { appointmentProposalOption: AppointmentProposalOption };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_FAILURE';
      payload: { error: string };
    }
  | { type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_CANCELLED' }
  | { type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION' }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS';
      payload: { appointmentProposalOption: AppointmentProposalOption };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE';
      payload: { error: string };
    }
  | { type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS' }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS';
      payload: { appointmentProposalOptions: AppointmentProposalOption[] };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION';
      payload: {
        appointmentProposalId: string;
        appointmentProposalOptionId: string;
        appointmentProposalOption: Partial<AppointmentProposalOption>;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS';
      payload: { appointmentProposalOption: AppointmentProposalOption };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT';
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_SUCCESS';
      payload: {
        appointmentProposalOptionId: string;
        appointmentProposalOptionCosts: AppointmentProposalOptionCosts;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'SHARE_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE';
      payload: { error: string };
    }
  | { type: 'GENERATE_DOCUMENT_SIGNATURE' }
  | {
      type: 'GENERATE_DOCUMENT_SIGNATURE_SUCCESS';
      payload: { appointmentProposalOption: AppointmentProposalOption };
    }
  | {
      type: 'GENERATE_DOCUMENT_SIGNATURE_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'SELECT_PROPOSAL_OPTIONS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'SELECT_OPTION_ID';
      payload: {
        appointmentProposalId: string;
        appointmentProposalOptionId: string;
      };
    }
  | { type: 'CLEAR_SELECTED_OPTION' }
  | {
      type: 'UPSERT_OPTIONS';
      payload: { appointmentProposalOptions: AppointmentProposalOption[] };
    }
  | {
      type: 'SETTLE_OPTIONS';
      payload: {
        appointmentProposalOptions: AppointmentProposalOption[];
        error?: string;
      };
    }
  | { type: 'SET_OPTIONS_MUTATING' }
  | {
      type: 'UPDATE_OPTION_COSTS';
      payload: {
        appointmentProposalOptionId: string;
        appointmentProposalOptionCosts: AppointmentProposalOptionCosts;
      };
    };

export const appointmentProposalOptionsReducer = (
  state: AppointmentProposalOptionsState,
  action: Action
): AppointmentProposalOptionsState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        selectedProposalId: action.payload.appointmentProposalId,
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS': {
      const fetchedOptions = action.payload.appointmentProposalOptions;
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedProposalIds:
          action.type === 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS'
            ? [
                ...new Set([
                  ...state.loadedProposalIds,
                  action.payload.appointmentProposalId,
                ]),
              ]
            : state.loadedProposalIds,
        ids: [
          ...new Set([
            ...state.ids,
            ...fetchedOptions.map((a) => a.appointmentProposalOptionId),
          ]),
        ],
        entities: fetchedOptions.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionId]: mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };
    }
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION':
      return {
        ...state,
        selectedId: action.payload.appointmentProposalOptionId,
        selectedProposalId: action.payload.appointmentProposalId,
        isLoaded: state.ids.includes(
          action.payload.appointmentProposalOptionId
        ),
        isLoading: true,
      };
    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION':
    case 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS':
      return {
        ...state,
        error: null,
        isLoading: true,
      };

    case 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointmentProposalOptions.map(
              (a) => a.appointmentProposalOptionId
            ),
          ]),
        ],
        entities: action.payload.appointmentProposalOptions.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionId]: curr,
          }),
          state.entities
        ),
      };
    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION':
      return {
        ...state,
        error: null,
        isLoading: true,
        entities: {
          ...state.entities,
          [action.payload.appointmentProposalOptionId]: mapOptionSyncErrors({
            ...state.entities[action.payload.appointmentProposalOptionId],
            ...action.payload.appointmentProposalOption,
          }),
        },
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_SUCCESS':
    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS':
    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_SUCCESS':
    case 'GENERATE_DOCUMENT_SIGNATURE_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            action.payload.appointmentProposalOption
              .appointmentProposalOptionId,
          ]),
        ],
        entities: {
          ...state.entities,
          [action.payload.appointmentProposalOption
            .appointmentProposalOptionId]: mapOptionSyncErrors(
            action.payload.appointmentProposalOption
          ),
        },
      };

    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT':
      return {
        ...state,
        error: null,
        isLoading: true,
      };

    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        costBreakdown: {
          ...state.costBreakdown,
          [action.payload.appointmentProposalOptionId]:
            action.payload.appointmentProposalOptionCosts,
        },
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE':
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_FAILURE':
    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE':
    case 'CREATE_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE':
    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_FAILURE':
    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_ENRICHMENT_FAILURE':
    case 'GENERATE_DOCUMENT_SIGNATURE_FAILURE':
    case 'SHARE_APPOINTMENT_PROPOSAL_OPTIONS_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_OPTIONS_CANCELLED':
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_CANCELLED':
      return {
        ...state,
        error: null,
      };

    case 'GENERATE_DOCUMENT_SIGNATURE':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'SELECT_PROPOSAL_OPTIONS':
      return {
        ...state,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        error: null,
        selectedProposalId: action.payload.appointmentProposalId,
      };

    case 'SELECT_OPTION_ID':
      return {
        ...state,
        isLoading: true,
        isLoaded: state.ids.includes(
          action.payload.appointmentProposalOptionId
        ),
        error: null,
        selectedId: action.payload.appointmentProposalOptionId,
        selectedProposalId: action.payload.appointmentProposalId,
      };

    case 'CLEAR_SELECTED_OPTION':
      return {
        ...state,
        selectedId: null,
        error: null,
      };

    case 'SET_OPTIONS_MUTATING':
      return { ...state, isLoading: true, error: null };

    case 'UPSERT_OPTIONS': {
      const upsertedOptions = action.payload.appointmentProposalOptions;
      return {
        ...state,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            ...upsertedOptions.map((o) => o.appointmentProposalId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...upsertedOptions.map((a) => a.appointmentProposalOptionId),
          ]),
        ],
        entities: upsertedOptions.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionId]: {
              ...mapOptionSyncErrors(curr),
              status:
                prev[curr.appointmentProposalOptionId]?.status === 'DELETED'
                  ? 'DELETED'
                  : curr.status,
            },
          }),
          state.entities
        ),
      };
    }

    case 'SETTLE_OPTIONS': {
      const settled = action.payload.appointmentProposalOptions;
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error ?? null,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            ...(state.selectedProposalId ? [state.selectedProposalId] : []),
            ...settled.map((o) => o.appointmentProposalId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...settled.map((o) => o.appointmentProposalOptionId),
          ]),
        ],
        entities: settled.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionId]: mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };
    }

    case 'UPDATE_OPTION_COSTS':
      return {
        ...state,
        costBreakdown: {
          ...state.costBreakdown,
          [action.payload.appointmentProposalOptionId]:
            action.payload.appointmentProposalOptionCosts,
        },
      };

    default:
      return state;
  }
};
